import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Terminal, Loader2, AlertCircle, HelpCircle, Copy, Check } from 'lucide-react';
import Editor from '@monaco-editor/react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { CustomDropdown } from '../../components/CustomDropdown ';
import { useClientLevel } from '../../hooks/useClientLevel';
import { useTheme } from '../../context/ThemeContext';

interface CustomScriptModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editingScript?: any; // If provided, we are in Edit mode
}

// OS Selection Options
const OS_OPTIONS = [
    { id: 'linux', label: 'Linux', img: '/linux.svg', defaultType: 'shell' },
    { id: 'windows', label: 'Windows', img: '/windows.png', defaultType: 'powershell' },
    { id: 'mac', label: 'macOS', img: '/mac.png', defaultType: 'shell' }
];

// UTF-8 Safe Base64 Helper
const encodeBase64 = (str: string): string => {
    try {
        return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => {
            return String.fromCharCode(parseInt(p1, 16));
        }));
    } catch (e) {
        console.error('Error encoding to base64', e);
        return '';
    }
};

const decodeBase64 = (base64Str: string): string => {
    try {
        if (!base64Str) return '';
        return decodeURIComponent(Array.prototype.map.call(atob(base64Str), (c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
    } catch (e) {
        console.error('Error decoding from base64', e);
        return '';
    }
};

const CustomScriptModal: React.FC<CustomScriptModalProps> = ({ isOpen, onClose, onSuccess, editingScript }) => {
    const [selectedOS, setSelectedOS] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [scriptType, setScriptType] = useState('shell');
    const [timeout, setTimeoutVal] = useState<number>(300);
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [copied, setCopied] = useState(false);
    const level = useClientLevel();
    const clientId = level.id;
    const { theme } = useTheme();

    const handleCopyCode = () => {
        if (!content) return;
        navigator.clipboard.writeText(content);
        setCopied(true);
        showSuccess("Script content copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Reset or populate form when modal opens or editingScript changes
    useEffect(() => {
        if (isOpen) {
            if (editingScript) {
                setName(editingScript.name || '');
                setDescription(editingScript.description || '');
                setScriptType(editingScript.script_type || 'shell');
                setTimeoutVal(editingScript.timeout ?? 300);
                setContent(decodeBase64(editingScript.content || ''));

                // Infer OS from script_type
                if (editingScript.script_type === 'powershell' || editingScript.script_type === 'batch') {
                    setSelectedOS('windows');
                } else if (editingScript.script_type === 'shell') {
                    // Default to linux if it's shell, or mac could also be shell. 
                    // Let's check if there is metadata, otherwise default to linux.
                    setSelectedOS(editingScript.target_os || 'linux');
                } else {
                    setSelectedOS('linux');
                }
            } else {
                // Reset form
                setSelectedOS(null);
                setName('');
                setDescription('');
                setScriptType('shell');
                setTimeoutVal(300);
                setContent('');
                setSubmitted(false);
            }
        }
    }, [isOpen, editingScript]);

    // Update Script Type when OS selection changes (only if creating a new script)
    const handleOSChange = (osId: string) => {
        setSelectedOS(osId);
        const option = OS_OPTIONS.find(o => o.id === osId);
        if (option) {
            setScriptType(option.defaultType);
        }
    };

    // File Import Logic
    const handleFileImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const extension = file.name.split('.').pop()?.toLowerCase();

        // Validation based on OS
        if (selectedOS === 'windows') {
            if (extension !== 'ps1' && extension !== 'bat') {
                showError('For Windows, only .ps1 and .bat files are supported.');
                e.target.value = '';
                return;
            }
            // Auto update type
            setScriptType(extension === 'ps1' ? 'powershell' : 'batch');
        } else if (selectedOS === 'linux' || selectedOS === 'mac') {
            if (extension !== 'sh') {
                showError(`For ${selectedOS === 'linux' ? 'Linux' : 'macOS'}, only .sh files are supported.`);
                e.target.value = '';
                return;
            }
            setScriptType('shell');
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const fileText = event.target?.result as string;
            setContent(fileText || '');
            showSuccess(`Imported ${file.name} successfully`);
        };
        reader.readAsText(file);

        // Reset file input value so same file can be selected again
        e.target.value = '';
    };

    // Handle form submit
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitted(true);

        if (!selectedOS) {
            showError('Please select a target operating system first');
            return;
        }

        if (!name.trim()) {
            showError('Please enter a script name');
            return;
        }

        if (!content.trim()) {
            showError('Please enter the script content or import a file');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                name: name.trim(),
                description: description.trim(),
                script_type: scriptType,
                content: encodeBase64(content),
                timeout: Number(timeout) || 300,
                client_id: clientId,
                // Include target_os field in payload just in case
            };

            const response = editingScript
                ? await baseapi.put(`/custom_scripts/${editingScript.id}`, payload)
                : await baseapi.post('/custom_scripts', payload);

            if (response.data.status) {
                showSuccess(editingScript ? 'Script updated successfully, Waiting for the approval for Execution' : 'Script saved successfully, Waiting for the approval for Execution');
                onSuccess();
                onClose();
            } else {
                showError(response.data.message || 'Operation failed');
            }
        } catch (error: any) {
            showError(error.response?.data?.message || error.message || 'An error occurred while saving the script');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // Map script type to Monaco editor language
    const getEditorLanguage = () => {
        if (scriptType === 'powershell') return 'powershell';
        if (scriptType === 'batch') return 'bat';
        return 'shell';
    };

    // Accepted file types for file dialog helper
    const getAcceptedExtensions = () => {
        if (selectedOS === 'windows') return '.ps1,.bat';
        return '.sh';
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            {/* Modal Body */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] relative flex flex-col overflow-hidden zoom-in-95">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
                    <div className="flex items-center gap-2">
                        <div className="bg-orange-500/10 dark:bg-orange-600/20 p-1.5 rounded-lg border border-orange-500/30">
                            <Terminal size={18} className="text-orange-600 dark:text-orange-500" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-wide">
                            {editingScript ? 'Edit Custom Script' : 'Add Custom Script'}
                        </h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Form and Editor Container */}
                <div className="flex-1 overflow-hidden flex flex-col md:flex-row">

                    {/* Left Panel: Form Settings */}
                    <div className="w-full md:w-[350px] border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-800 p-5 flex flex-col gap-4 overflow-y-auto custom-scroll bg-gray-50/30 dark:bg-transparent">

                        {/* OS Selection */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-1">
                                <span>1. Target Operating System</span>
                                <span className="text-rose-500">*</span>
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                {OS_OPTIONS.map((os) => (
                                    <button
                                        key={os.id}
                                        type="button"
                                        onClick={() => handleOSChange(os.id)}
                                        className={`flex flex-col items-center justify-center py-2.5 rounded-lg border transition-all duration-200 ${selectedOS === os.id
                                            ? 'bg-orange-500/10 border-orange-500 text-orange-600 dark:text-white shadow-sm'
                                            : 'bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-700 hover:text-gray-900 dark:hover:text-gray-200'
                                            }`}
                                    >
                                        <img src={os.img} alt={os.label} className="w-6 h-6" />
                                        <span className="text-[10px] font-bold tracking-wider mt-1">{os.label}</span>
                                    </button>
                                ))}
                            </div>
                            {submitted && !selectedOS && (
                                <p className="text-[9px] text-rose-500 font-bold uppercase tracking-widest">OS Selection is required</p>
                            )}
                        </div>

                        {/* Script Fields - Disabled until OS is selected */}
                        <div className={`space-y-4 flex-1 flex flex-col justify-start transition-opacity duration-200 ${selectedOS ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>

                            {/* Script Name */}
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Script Name *</label>
                                <input
                                    type="text"
                                    disabled={!selectedOS}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Disk Cleanup Utility"
                                    className={`w-full bg-white dark:bg-gray-950 border rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none transition-all ${submitted && !name.trim() ? 'border-rose-500 focus:border-rose-500' : 'border-gray-200 dark:border-gray-800 focus:border-orange-500'
                                        }`}
                                />
                                {submitted && !name.trim() && (
                                    <p className="text-[9px] text-rose-500 font-bold uppercase tracking-widest">Name is required</p>
                                )}
                            </div>

                            {/* Description */}
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Description</label>
                                <textarea
                                    disabled={!selectedOS}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Enter script description..."
                                    rows={3}
                                    className="w-full bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-orange-500 resize-none transition-all"
                                />
                            </div>

                            {/* Timeout */}
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-1">
                                    <span>Timeout (Seconds)</span>
                                    <HelpCircle size={12} className="text-gray-400 dark:text-gray-500 cursor-help" />
                                </label>
                                <input
                                    type="number"
                                    disabled={!selectedOS}
                                    value={timeout}
                                    onChange={(e) => setTimeoutVal(Math.max(1, Number(e.target.value)))}
                                    placeholder="300"
                                    min="1"
                                    className="w-full bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white focus:outline-none focus:border-orange-500 transition-all"
                                />
                            </div>

                            {/* Script Type & File Import */}
                            <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-800">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Script Type</label>
                                    <CustomDropdown
                                        data={
                                            selectedOS === 'windows'
                                                ? [
                                                    { label: 'Powershell (.ps1)', value: 'powershell' },
                                                    { label: 'Batch (.bat)', value: 'batch' }
                                                ]
                                                : [
                                                    { label: 'Shell (.sh)', value: 'shell' }
                                                ]
                                        }
                                        selectedValue={scriptType}
                                        onSelect={(val) => setScriptType(val)}
                                        disabled={!selectedOS || selectedOS !== 'windows'}
                                        width="w-full"
                                        placeholder="Select script type..."
                                        buttonHeight={10}
                                        className="text-xs"
                                    />
                                </div>

                                {/* Import local file */}
                                <div>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        accept={getAcceptedExtensions()}
                                        className="hidden"
                                    />
                                    <button
                                        type="button"
                                        disabled={!selectedOS}
                                        onClick={handleFileImportClick}
                                        className="w-full flex items-center justify-center gap-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-xs font-bold py-2 rounded-lg transition-all shadow-sm"
                                    >
                                        <Upload size={14} className="text-orange-600 dark:text-orange-500" />
                                        <span>Import Local File</span>
                                    </button>
                                    <p className="text-[9px] text-gray-500 mt-1 text-center font-medium italic">
                                        Accepts {getAcceptedExtensions()} files
                                    </p>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Right Panel: Monaco Editor */}
                    <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-950 min-w-0 h-[50vh] md:h-full">

                        {/* Editor Header Bar */}
                        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-800 bg-gray-100/60 dark:bg-gray-950">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-cyan-500" />
                                <span className="text-[10px] font-black text-gray-600 dark:text-gray-400 uppercase tracking-widest">
                                    2. Code Editor ({getEditorLanguage()})
                                </span>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleCopyCode}
                                    disabled={!content}
                                    className="flex items-center gap-1.5 px-2.5 py-1 bg-white hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 rounded text-[10px] font-bold text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                                    title="Copy code to clipboard"
                                >
                                    {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} className="text-gray-400" />}
                                    <span>{copied ? "Copied!" : "Copy"}</span>
                                </button>
                                {!selectedOS && (
                                    <div className="flex items-center gap-1.5 text-orange-600 dark:text-orange-400 text-[9px] font-black uppercase tracking-widest">
                                        <AlertCircle size={12} />
                                        <span>Select target OS to enable editor</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Editor Area */}
                        <div
                            className="flex-1 relative bg-white dark:bg-gray-950"
                            style={{ height: '100%', minHeight: '350px', width: '100%' }}
                        >
                            {!selectedOS && (
                                <div className="absolute inset-0 bg-white/80 dark:bg-gray-950/80 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center text-gray-600 dark:text-gray-400 p-4 text-center">
                                    <Terminal size={32} className="opacity-30 mb-3" />
                                    <p className="text-sm font-semibold">Operating System Not Selected</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-600 mt-1">Please select Linux, Windows, or macOS on the left menu to start coding.</p>
                                </div>
                            )}
                            <Editor
                                height="100%"
                                language={getEditorLanguage()}
                                theme={theme === 'dark' ? 'vs-dark' : 'light'}
                                value={content}
                                onChange={(val) => setContent(val || '')}
                                options={{
                                    minimap: { enabled: false },
                                    fontSize: 12,
                                    lineNumbers: 'on',
                                    automaticLayout: true,
                                    scrollBeyondLastLine: false,
                                    padding: { top: 12, bottom: 12 }
                                }}
                            />
                        </div>

                        {/* Editor Footer Status */}
                        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-900 bg-gray-50 dark:bg-gray-950 flex justify-between items-center text-[10px] text-gray-500 font-mono">
                            <div>
                                Lines: {content.split('\n').length} | Characters: {content.length}
                            </div>
                            {submitted && !content.trim() && (
                                <span className="text-rose-500 font-bold uppercase tracking-wider">Script content is required</span>
                            )}
                        </div>

                    </div>
                </div>

                {/* Footer Controls */}
                <div className="flex bg-gray-50 dark:bg-gray-800/80 border-t border-gray-200 dark:border-gray-800 p-4 justify-end gap-3 z-10 shadow-lg shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2 text-xs font-bold text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-200 dark:border-gray-700 uppercase tracking-wider shadow-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-8 py-2 text-xs font-bold text-white bg-orange-600 hover:bg-orange-500 rounded-lg transition-colors shadow-lg shadow-orange-900/30 flex items-center gap-2 disabled:opacity-50 uppercase tracking-wider"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                <span>Saving...</span>
                            </>
                        ) : (
                            <span>{editingScript ? 'Update Script' : 'Save Script'}</span>
                        )}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default CustomScriptModal;
